"use client";

import { IconClockOutline18 } from "nucleo-ui-outline-18";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useOrganizations } from "@/hooks/useOrganizations";
import { renderTextWithOrganizationWallets } from "@/components/ui/organization-wallet-inline";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getRelativeTime } from "@/lib/utils/formatRelativeTime";
import {
  resolveDescriptionTemplateParts,
  type DescriptionFormatHints,
  type ResolvedTemplateFieldPart,
} from "@/utils/resolveDescriptionTemplates";
import { Fragment, useEffect, useMemo, useState } from "react";
import { InlineColorSwatch, InlineTokenBadge, type InlineTokenData } from "./InlineAddressLink";
import { ChainBadge } from "./ChainBadge";
import { containsTemplateVariable, renderValueWithVariables } from "./VariablePill";

interface ResolvedDescriptionContentProps {
  text?: string;
  payload?: Record<string, unknown>;
  formatHints?: DescriptionFormatHints;
  tokens?: InlineTokenData[];
}

const DATETIME_FIELD_RE = /(date|time|timestamp|deadline)$/i;
const CHAIN_FIELD_RE = /chainid|chain_ids?|srcchain|dstchain/i;
const TOKEN_FIELD_RE = /token(address|in|out|0|1|a|b)?$/i;
const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

function isChainField(part: ResolvedTemplateFieldPart): boolean {
  return CHAIN_FIELD_RE.test(part.fieldName);
}

function isTokenField(part: ResolvedTemplateFieldPart): boolean {
  return TOKEN_FIELD_RE.test(part.fieldName);
}

function getChainIds(parts: ReturnType<typeof resolveDescriptionTemplateParts>): number[] {
  return parts.flatMap(part => {
    if (part.type !== "field" || !part.resolved || !isChainField(part)) return [];
    if (typeof part.value === "number") return [part.value];
    if (typeof part.value === "string") {
      const parsed = Number(part.value);
      return Number.isNaN(parsed) ? [] : [parsed];
    }
    if (Array.isArray(part.value)) {
      return part.value
        .map(value => (typeof value === "number" ? value : Number(value)))
        .filter(value => !Number.isNaN(value) && value > 0);
    }
    return [];
  });
}

function formatPreciseDateTime(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function RelativeDateTimeBadge({ value }: { value: string }) {
  const [relativeTime, setRelativeTime] = useState(() => getRelativeTime(new Date(value)));

  useEffect(() => {
    const update = () => {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        setRelativeTime(getRelativeTime(date));
      }
    };

    update();
    const interval = window.setInterval(update, 60_000);
    return () => window.clearInterval(interval);
  }, [value]);

  const preciseTime = formatPreciseDateTime(value);
  if (!preciseTime) {
    return <>{value}</>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="bg-ds-neutral-100 text-ds-text-secondary dark:bg-ds-neutral-800 inline-flex cursor-help items-center gap-1 rounded px-1.5 py-0.5 align-middle text-xs">
          <IconClockOutline18 className="h-3 w-3 opacity-60" />
          <span>{relativeTime}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{preciseTime}</TooltipContent>
    </Tooltip>
  );
}

function renderFieldPart(
  part: ResolvedTemplateFieldPart,
  wallets: ReturnType<typeof useOrganizationWallets>["wallets"],
  tokensMap?: Map<string, InlineTokenData>,
  chainInfoMap?: Map<number, { chainId: number; name: string; iconUrl: string | null }>,
) {
  if (!part.resolved) {
    return renderTextWithOrganizationWallets(part.matchedText, wallets, tokensMap);
  }

  // Workflow expressions (e.g., {{$item.amount}}) → render as VariablePill
  if (typeof part.value === "string" && containsTemplateVariable(part.value)) {
    return renderValueWithVariables(part.value);
  }

  if (isChainField(part)) {
    const values = Array.isArray(part.value) ? part.value : [part.value];
    const chainIds = values
      .map(value => (typeof value === "number" ? value : Number(value)))
      .filter(value => !Number.isNaN(value) && value > 0);

    if (chainIds.length > 0) {
      return (
        <span className="inline-flex flex-wrap items-center gap-1 align-middle">
          {chainIds.map(chainId => {
            const chain = chainInfoMap?.get(chainId);
            return chain ? (
              <ChainBadge key={chainId} chain={chain} size="sm" variant="summary" />
            ) : (
              <span
                key={chainId}
                className="bg-ds-neutral-100 text-ds-text-secondary dark:bg-ds-neutral-800 inline-flex items-center rounded px-1.5 py-0.5 text-xs"
              >
                Chain {chainId}
              </span>
            );
          })}
        </span>
      );
    }
  }

  if (isTokenField(part) && typeof part.value === "string" && EVM_ADDRESS_RE.test(part.value)) {
    const token = tokensMap?.get(part.value.toLowerCase());
    if (token) {
      return <InlineTokenBadge token={token} variant="summary" />;
    }
  }

  if (typeof part.value === "string" && DATETIME_FIELD_RE.test(part.fieldName)) {
    const preciseTime = formatPreciseDateTime(part.value);
    if (preciseTime) {
      return <RelativeDateTimeBadge value={part.value} />;
    }
  }

  if (typeof part.value === "string" && EVM_ADDRESS_RE.test(part.value)) {
    return renderTextWithOrganizationWallets(part.value, wallets, tokensMap);
  }

  if (
    typeof part.displayValue === "string" &&
    /^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/.test(part.displayValue)
  ) {
    return <InlineColorSwatch color={part.displayValue} />;
  }

  return renderTextWithOrganizationWallets(part.displayValue, wallets, tokensMap);
}

export function ResolvedDescriptionContent({ text, payload, formatHints, tokens }: ResolvedDescriptionContentProps) {
  const parts = useMemo(
    () => resolveDescriptionTemplateParts(text, payload, formatHints),
    [text, payload, formatHints],
  );
  const chainIds = useMemo(() => getChainIds(parts), [parts]);
  const { chainInfoMap } = useChainInfoMap(chainIds);
  const { selectedOrgId } = useOrganizations();
  const { wallets } = useOrganizationWallets(selectedOrgId ?? undefined);
  const tokensMap = useMemo(
    () => (tokens ? new Map(tokens.map(token => [token.address.toLowerCase(), token])) : undefined),
    [tokens],
  );

  if (!text) return null;

  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={`${part.type}-${index}`}>
          {part.type === "text"
            ? renderTextWithOrganizationWallets(part.value, wallets, tokensMap)
            : renderFieldPart(part, wallets, tokensMap, chainInfoMap)}
        </Fragment>
      ))}
    </>
  );
}
